import { endOfYear } from "date-fns";

import { baseUrl } from "util/APIBaseUrl";
import postRequest from "util/APIHelperPost";
import { makeRequest } from "util/APIHelper";
import getRequest from "util/APIHelperGet";
import { USER_TYPES } from "constants/index";
import { formatDateAsLocal, getDaysDifferenceBetweenDates } from "util/utils";

const getLoggedInUser = () => JSON.parse(localStorage.getItem("loggedInUser"));

export const createLeaveRequest = async ({ selectedDateRange, selectedLeaveType, description }) => {
  const loggedInUser = getLoggedInUser();
  const { startDate: rawStartDate, endDate: rawEndDate } = selectedDateRange || {};

  const startDate = formatDateAsLocal(rawStartDate);
  const endDate = formatDateAsLocal(rawEndDate);

  const daysDifference = getDaysDifferenceBetweenDates(startDate, endDate);

  let body = {
    leaveTypeId: selectedLeaveType,
    employeeId: loggedInUser?.userId,
    dateFrom: startDate,
    dateTo: endDate,
    noOfLeaves: daysDifference,
    description,
  };

  const request = `${baseUrl}/employee/leave`;
  body = JSON.stringify(body);

  const res = await postRequest(request, body);
  return res;
};

export const createLeaveRequestV2 = async ({
  selectedDateRange,
  selectedLeaveType,
  description,
  leaveDocuments,
  duration,
  swapDays,
}) => {
  const loggedInUser = getLoggedInUser();
  const { startDate: rawStartDate, endDate: rawEndDate } = selectedDateRange || {};

  const startDate = formatDateAsLocal(rawStartDate);
  const endDate = formatDateAsLocal(rawEndDate);

  const daysDifference = getDaysDifferenceBetweenDates(startDate, endDate);

  let body = {
    leaveTypeId: selectedLeaveType,
    employeeId: loggedInUser?.userId,
    dateFrom: startDate,
    dateTo: endDate,
    noOfLeaves: daysDifference,
    description,
    documents: leaveDocuments,
  };

  if (duration) {
    body.duration = duration;
  }

  // Add swap dates for swapped leave type
  if (swapDays && swapDays.startDate && swapDays.endDate) {
    body.swapDateFrom = formatDateAsLocal(swapDays.startDate);
    body.swapDateTo = formatDateAsLocal(swapDays.endDate);
  }

  const request = `${baseUrl}/v3/employee/leave`;
  body = JSON.stringify(body);

  const res = await postRequest(request, body);
  return res;
};

export const approveOrRejectLeaveRequest = async ({
  customerId,
  requestId,
  action,
  replacementData,
  reason = "",
  loggedInUserType,
  requestReplacement = false,
  anniversaryBasedTimeOff = false,
}) => {
  let url;

  if (loggedInUserType === USER_TYPES.EDGE) {
    url = `${baseUrl}${anniversaryBasedTimeOff ? "/v2" : ""}/admin/leave/${requestId}/action`;
  } else if (requestReplacement) {
    url = `${baseUrl}${anniversaryBasedTimeOff ? "/v2" : ""}/customer/${customerId}/leave/${requestId}/replacement`;
  } else {
    url = `${baseUrl}${anniversaryBasedTimeOff ? "/v2" : ""}/customer/${customerId}/leave/${requestId}`;
  }
  return await makeRequest(
    {
      path: url,
      method: "PUT",
      body: requestReplacement
        ? JSON.stringify({
            reportsTo: replacementData.reportsTo,
            credentials: replacementData.credentials,
            tasks: replacementData.tasks,
          })
        : JSON.stringify({
            action,
            reason,
            ...replacementData,
          }),
      contentType: "application/json",
    },
    false
  );
};

export const getCustomerEmployeeLeaves = async (employeeId, anniversaryBasedTimeOff = false) => {
  const loggedInUser = getLoggedInUser();
  const customerKey = loggedInUser?.customerId || loggedInUser?.pendingCustomerId;
  return await getRequest(
    `${baseUrl}${anniversaryBasedTimeOff ? "/v2" : ""}/customer/${customerKey}/employee/${employeeId}/upcoming_leaves`,
    true
  );
};

export const getCustomerLeavesCalender = async (earliestStartDate) => {
  const loggedInUser = getLoggedInUser();
  const customerKey = loggedInUser?.customerId || loggedInUser?.pendingCustomerId;
  const currentDate = new Date();
  const endOfCurrentYear = endOfYear(currentDate);
  const res = await getRequest(
    `${baseUrl}/customer/${customerKey}/calendar?startDate=${earliestStartDate}&endDate=${endOfCurrentYear}`,
    true
  );
  return res;
};

export const getEmployeesTimeout = async (dateRange) => {
  const loggedInUser = getLoggedInUser();
  const customerKey = loggedInUser?.customerId || loggedInUser?.pendingCustomerId;
  const res = await getRequest(
    `${baseUrl}/customer/${customerKey}/leave_employees?startDate=${dateRange.startDate}&endDate=${dateRange?.endDate}`,
    true
  );
  return res;
};

export const getEmployeeLeavesDataForCustomer = async (employeeId, anniversaryBasedTimeOff) => {
  const loggedInUser = getLoggedInUser();
  const customerKey = loggedInUser?.customerId || loggedInUser?.pendingCustomerId;
  const adjustedBaseUrl = anniversaryBasedTimeOff ? `${baseUrl}/v2` : baseUrl;
  const requestUrl = `${adjustedBaseUrl}/customer/${customerKey}/employee/${employeeId}/leave_types`;

  const res = await getRequest(requestUrl, true);
  return res;
};

export const getEmployeePendingLeaves = async (employeeId, anniversaryBasedTimeOff = false) => {
  const loggedInUser = getLoggedInUser();
  const customerKey = loggedInUser?.customerId || loggedInUser?.pendingCustomerId;
  const res = await getRequest(
    `${baseUrl}${anniversaryBasedTimeOff ? "/v2" : ""}/customer/${customerKey}/employee/${employeeId}/leave`,
    true
  );
  return res;
};

export const uploadMedicalDocs = async (leaveId, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

  const res = await postRequest(`${baseUrl}/employee/leave/document/${leaveId}`, formData, true, "POST", true);
  return res;
};

export const uploadMedicalDocsV2 = async (leaveId, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

  const res = await postRequest(`${baseUrl}/v2/employee/leave/document/${leaveId}`, formData, true, "POST", true);
  return res;
};

export const getMedicalDocsListing = async (leaveId) => {
  const res = await getRequest(`${baseUrl}/employee/leave/document/${leaveId}`, true);
  return res;
};

export const downloadLeaveDocument = async (documentId, documentName, employeeId) => {
  const { customerId, pendingCustomerId, userId } = JSON.parse(localStorage.getItem("loggedInUser"));
  const customerKey = customerId || pendingCustomerId;

  const effectiveEmployeeId = employeeId || userId;

  const response = await getRequest(
    `${baseUrl}/customer/${customerKey}/employee/${effectiveEmployeeId}/leave_request/document/${documentId}/download-url`
  );
  if (!response?.ok) {
    return;
  }

  const url = response.payload.data;
  const blob = await fetch(url).then((res) => res.blob());
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = blobUrl;
  link.download = documentName;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(blobUrl);
};

export const getMedicalDocsListingV2 = async (leaveId) => {
  const res = await getRequest(`${baseUrl}/v2/employee/leave/document/${leaveId}`, true);
  return res;
};

export const downloadMedicalDoc = async (leaveRequestId, documentId, name = "medical-doc") => {
  const res = await getRequest(`${baseUrl}/employee/leave/document/${leaveRequestId}/${documentId}`, true, "export");

  if (!res?.ok) {
    return null;
  }
  const url = window.URL.createObjectURL(res?.payload);
  const link = document?.createElement("a");
  link.style.display = "none";
  link.href = url;
  link.download = name;

  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  return res;
};

export const downloadMedicalDocV2 = async (leaveRequestId, documentId, name = "medical-doc") => {
  const res = await getRequest(`${baseUrl}/v2/employee/leave/document/${leaveRequestId}/${documentId}`, true, "export");

  if (!res?.ok) {
    return null;
  }
  const url = window.URL.createObjectURL(res?.payload);
  const link = document?.createElement("a");
  link.style.display = "none";
  link.href = url;
  link.download = name;

  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  return res;
};

export const getLeaveCount = async (startDate, endDate, leaveTypeId, excludeRequestId = null) => {
  const loggedInUser = getLoggedInUser();
  let url = `${baseUrl}/employee/${loggedInUser.userId}/count_leaves/${leaveTypeId}?dateFrom=${formatDateAsLocal(startDate)}&dateTo=${formatDateAsLocal(endDate)}`;
  if (excludeRequestId) {
    url += `&excludeRequestId=${excludeRequestId}`;
  }
  const res = await getRequest(url, true);
  return res;
};

export const getLeavePoliciesAPI = async (country = "") => {
  const { payload } = await getRequest(`${baseUrl}/leave_policies?country=${country}`, true);
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description);
  }
  return payload;
};

export const getLeavePolicyDetailsAPI = async (policyId) => {
  const { payload } = await getRequest(`${baseUrl}/leave_policies/${policyId}`, true);
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description);
  }
  return payload;
};

export const getAllCountriesLeavePolicies = async () => {
  const { payload } = await getRequest(`${baseUrl}/leave_policies/countries`, true);
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description);
  }
  return payload;
};

// PRD-442: V4 Future Cycle Leave Filing
export const createLeaveRequestV4 = async ({
  selectedDateRange,
  selectedLeaveType,
  description,
  leaveDocuments,
  duration,
  swapDays,
}) => {
  const loggedInUser = getLoggedInUser();
  const { startDate: rawStartDate, endDate: rawEndDate } = selectedDateRange || {};

  const startDate = formatDateAsLocal(rawStartDate);
  const endDate = formatDateAsLocal(rawEndDate);

  const daysDifference = getDaysDifferenceBetweenDates(startDate, endDate);

  let body = {
    leaveTypeId: selectedLeaveType,
    employeeId: loggedInUser?.userId,
    dateFrom: startDate,
    dateTo: endDate,
    noOfLeaves: daysDifference,
    description,
    documents: leaveDocuments,
  };

  if (duration) {
    body.duration = duration;
  }

  if (swapDays && swapDays.startDate && swapDays.endDate) {
    body.swapDateFrom = formatDateAsLocal(swapDays.startDate);
    body.swapDateTo = formatDateAsLocal(swapDays.endDate);
  }

  const request = `${baseUrl}/v4/employee/leave`;
  body = JSON.stringify(body);

  const res = await postRequest(request, body);
  return res;
};

export const editLeaveRequest = async ({
  requestId,
  selectedDateRange,
  selectedLeaveType,
  description,
  duration,
  employeeId,
}) => {
  const loggedInUser = getLoggedInUser();
  // When an Edge admin edits on an employee's behalf, employeeId is the leave
  // owner; otherwise the employee edits their own leave (logged-in user).
  const targetEmployeeId = employeeId || loggedInUser?.userId;
  const { startDate: rawStartDate, endDate: rawEndDate } = selectedDateRange || {};

  const startDate = formatDateAsLocal(rawStartDate);
  const endDate = formatDateAsLocal(rawEndDate);

  const daysDifference = getDaysDifferenceBetweenDates(startDate, endDate);

  const body = {
    leaveTypeId: selectedLeaveType,
    employeeId: targetEmployeeId,
    dateFrom: startDate,
    dateTo: endDate,
    noOfLeaves: daysDifference,
    description,
  };

  if (duration) {
    body.duration = duration;
  }

  const path = `${baseUrl}/employee/${targetEmployeeId}/leave-requests/${requestId}`;
  return makeRequest({ path, method: "PUT", body: JSON.stringify(body), contentType: "application/json" }, false);
};

export const getLeaveCountV4 = async (
  startDate,
  endDate,
  leaveTypeId,
  duration,
  swapDateFrom,
  swapDateTo,
  swappedLeavesRevamp,
  excludeRequestId = null
) => {
  const loggedInUser = getLoggedInUser();
  let url = `${baseUrl}/v4/employee/${loggedInUser.userId}/count_leaves/${leaveTypeId}?dateFrom=${formatDateAsLocal(startDate)}&dateTo=${formatDateAsLocal(endDate)}`;
  if (duration) {
    url += `&duration=${duration}`;
  }
  if (swapDateFrom && swapDateTo && swappedLeavesRevamp) {
    url += `&swapDateFrom=${formatDateAsLocal(swapDateFrom)}&swapDateTo=${formatDateAsLocal(swapDateTo)}`;
  }
  if (excludeRequestId) {
    url += `&excludeRequestId=${excludeRequestId}`;
  }
  const res = await getRequest(url, true);
  return res;
};

export const getLeaveCountV2 = async (
  startDate,
  endDate,
  leaveTypeId,
  duration,
  swapDateFrom,
  swapDateTo,
  swappedLeavesRevamp,
  excludeRequestId = null
) => {
  const loggedInUser = getLoggedInUser();
  let url = `${baseUrl}/v2/employee/${loggedInUser.userId}/count_leaves/${leaveTypeId}?dateFrom=${formatDateAsLocal(startDate)}&dateTo=${formatDateAsLocal(endDate)}`;
  if (duration) {
    url += `&duration=${duration}`;
  }

  if (swapDateFrom && swapDateTo && swappedLeavesRevamp) {
    url += `&swapDateFrom=${formatDateAsLocal(swapDateFrom)}&swapDateTo=${formatDateAsLocal(swapDateTo)}`;
  }
  if (excludeRequestId) {
    url += `&excludeRequestId=${excludeRequestId}`;
  }
  const res = await getRequest(url, true);
  return res;
};
